import React, { useState } from 'react';
import { Card, Btn, Badge, Modal, FormRow, SectionTitle, EmptyState } from '../components/UI';
import { format, parseISO } from 'date-fns';

const PLANT_TYPES = ['Vegetable', 'Fruit', 'Herb', 'Tree', 'Fruit bushes & canes', 'Stone fruit', 'Nut trees', 'Flower', 'Shrub', 'Other'];
const STATUSES = ['Seedling', 'Growing', 'Flowering', 'Fruiting', 'Dormant', 'Harvested'];
const CARE_TYPES = ['Watered', 'Fertilised', 'Pruned', 'Repotted', 'Treated', 'Observed', 'Other'];

function statusColor(s) {
  if (!s) return 'stone';
  if (['Fruiting', 'Flowering'].includes(s)) return 'green';
  if (['Growing', 'Seedling'].includes(s)) return 'soil';
  if (s === 'Dormant') return 'stone';
  return 'stone';
}

function formatDate(d) {
  try { return format(parseISO(d), 'd MMM yy'); } catch { return d || ''; }
}

async function fetchPlantImage(name) {
  // Try progressively shorter queries: full name → genus+species → genus only
  const attempts = [name, name.split(' ').slice(0, 2).join(' '), name.split(' ')[0]]
    .filter((v, i, a) => v && a.indexOf(v) === i);

  for (const query of attempts) {
    try {
      // REST summary API
      const res = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(query)}`);
      const data = await res.json();
      if (data.thumbnail?.source) return data.thumbnail.source;
    } catch (e) {}
    try {
      // MediaWiki pageimages fallback
      const res = await fetch(`https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(query)}&prop=pageimages&format=json&pithumbsize=400&origin=*`);
      const data = await res.json();
      const page = Object.values(data?.query?.pages || {})[0];
      if (page?.thumbnail?.source) return page.thumbnail.source;
    } catch (e) {}
  }
  return null;
}

// Row view for the plant list
function PlantRow({ plant, onClick }) {
  const totalHarvest = (plant.harvestLog || []).reduce((s, h) => s + (parseFloat(h.amount) || 0), 0);
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '10px 12px', borderBottom: '1px solid var(--border)',
      cursor: 'pointer', borderRadius: 'var(--radius-sm)',
      transition: 'background 0.12s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🌿</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{plant.name}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 1 }}>
          {[plant.type, plant.location, plant.sowDate ? formatDate(plant.sowDate) : ''].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {totalHarvest > 0 && <span style={{ fontSize: 11, color: 'var(--moss-600)' }}>🌾 {totalHarvest.toFixed(1)}kg</span>}
        {plant.status && <Badge color={statusColor(plant.status)}>{plant.status}</Badge>}
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>→</span>
      </div>
    </div>
  );
}

function AddPlantModal({ onSave, onClose }) {
  const [form, setForm] = useState({ name: '', variety: '', type: 'Vegetable', status: 'Seedling', location: '', sowDate: '', waterEvery: '', notes: '' });
  const [fetching, setFetching] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!form.name.trim()) return;
    setFetching(true);
    const image = await fetchPlantImage(form.name);
    setFetching(false);
    onSave({ id: 'p_' + Date.now(), ...form, image, careLog: [], harvestLog: [], lastWatered: null, createdAt: new Date().toISOString() });
  }

  return (
    <Modal title="Add plant" onClose={onClose}>
      <FormRow label="Name"><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Courgette" autoFocus /></FormRow>
      <FormRow label="Variety"><input value={form.variety} onChange={e => set('variety', e.target.value)} placeholder="e.g. Defender" /></FormRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormRow label="Type">
          <select value={form.type} onChange={e => set('type', e.target.value)}>
            {PLANT_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </FormRow>
        <FormRow label="Status">
          <select value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </FormRow>
      </div>
      <FormRow label="Location / bed"><input value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Bed A, Greenhouse" /></FormRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormRow label="Sow / plant date"><input type="date" value={form.sowDate} onChange={e => set('sowDate', e.target.value)} /></FormRow>
        <FormRow label="Water every (days)"><input type="number" min="1" value={form.waterEvery} onChange={e => set('waterEvery', e.target.value)} placeholder="7" /></FormRow>
      </div>
      <FormRow label="Notes"><textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes…" /></FormRow>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSave} disabled={fetching || !form.name.trim()}>{fetching ? 'Fetching image…' : 'Add plant'}</Btn>
      </div>
    </Modal>
  );
}

function EditPlantModal({ plant, onSave, onClose }) {
  const [form, setForm] = useState({
    name: plant.name || '', variety: plant.variety || '', type: plant.type || 'Vegetable',
    status: plant.status || 'Growing', location: plant.location || '',
    sowDate: plant.sowDate || '', waterEvery: plant.waterEvery || '', notes: plant.notes || '',
  });
  const [refetching, setRefetching] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleRefetchImage() {
    setRefetching(true);
    const image = await fetchPlantImage(form.name);
    setRefetching(false);
    onSave({ ...plant, ...form, image: image || plant.image });
  }

  return (
    <Modal title={`Edit — ${plant.name}`} onClose={onClose}>
      <FormRow label="Name"><input value={form.name} onChange={e => set('name', e.target.value)} autoFocus /></FormRow>
      <FormRow label="Variety"><input value={form.variety} onChange={e => set('variety', e.target.value)} /></FormRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormRow label="Type">
          <select value={form.type} onChange={e => set('type', e.target.value)}>
            {PLANT_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </FormRow>
        <FormRow label="Status">
          <select value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </FormRow>
      </div>
      <FormRow label="Location / bed"><input value={form.location} onChange={e => set('location', e.target.value)} /></FormRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormRow label="Sow / plant date"><input type="date" value={form.sowDate} onChange={e => set('sowDate', e.target.value)} /></FormRow>
        <FormRow label="Water every (days)"><input type="number" min="1" value={form.waterEvery} onChange={e => set('waterEvery', e.target.value)} /></FormRow>
      </div>
      <FormRow label="Notes"><textarea value={form.notes} onChange={e => set('notes', e.target.value)} /></FormRow>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Btn size="sm" onClick={handleRefetchImage} disabled={refetching}>{refetching ? '…' : '↻ Refresh image'}</Btn>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={() => onSave({ ...plant, ...form })} disabled={!form.name.trim()}>Save changes</Btn>
      </div>
    </Modal>
  );
}

function LogCareModal({ plant, onSave, onClose }) {
  const [form, setForm] = useState({ type: 'Watered', date: format(new Date(), 'yyyy-MM-dd'), note: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Modal title={`Log care — ${plant.name}`} onClose={onClose} width={400}>
      <FormRow label="Type">
        <select value={form.type} onChange={e => set('type', e.target.value)}>
          {CARE_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
      </FormRow>
      <FormRow label="Date"><input type="date" value={form.date} onChange={e => set('date', e.target.value)} /></FormRow>
      <FormRow label="Note"><textarea value={form.note} onChange={e => set('note', e.target.value)} placeholder="Optional note…" /></FormRow>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={() => onSave({ id: 'c_' + Date.now(), ...form })}>Save</Btn>
      </div>
    </Modal>
  );
}

function LogHarvestModal({ plant, onSave, onClose }) {
  const [form, setForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), amount: '', unit: 'kg', note: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Modal title={`Log harvest — ${plant.name}`} onClose={onClose} width={400}>
      <FormRow label="Date"><input type="date" value={form.date} onChange={e => set('date', e.target.value)} /></FormRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 12 }}>
        <FormRow label="Amount"><input type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.5" /></FormRow>
        <FormRow label="Unit">
          <select value={form.unit} onChange={e => set('unit', e.target.value)}>
            {['kg', 'g', 'lb', 'count', 'bunch'].map(u => <option key={u}>{u}</option>)}
          </select>
        </FormRow>
      </div>
      <FormRow label="Note"><textarea value={form.note} onChange={e => set('note', e.target.value)} placeholder="Quality, appearance…" /></FormRow>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={() => onSave({ id: 'h_' + Date.now(), ...form })} disabled={!form.amount}>Log harvest</Btn>
      </div>
    </Modal>
  );
}

function PlantDetail({ plant, onUpdate, onDelete, onBack }) {
  const [careModal, setCareModal] = useState(false);
  const [harvestModal, setHarvestModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);

  const totalHarvest = (plant.harvestLog || []).reduce((s, h) => s + (parseFloat(h.amount) || 0), 0);

  async function refetchImage() {
    setLoadingImage(true);
    const image = await fetchPlantImage(plant.name);
    onUpdate({ ...plant, image: image || plant.image });
    setLoadingImage(false);
  }

  function addCare(entry) {
    const updated = { ...plant, careLog: [...(plant.careLog || []), entry] };
    if (entry.type === 'Watered') updated.lastWatered = entry.date;
    onUpdate(updated);
    setCareModal(false);
  }

  function addHarvest(entry) {
    onUpdate({ ...plant, harvestLog: [...(plant.harvestLog || []), entry] });
    setHarvestModal(false);
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 800 }} className="fade-up">
      <button onClick={onBack} style={{ fontSize: 13, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 6 }}>
        ← Back to plants
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ height: 200, borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--bg-subtle)', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {plant.image ? (
              <img src={plant.image} alt={plant.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ fontSize: 48, opacity: 0.4 }}>🌿</div>
            )}
          </div>
          <Btn size="sm" onClick={refetchImage} disabled={loadingImage} style={{ width: '100%' }}>
            {loadingImage ? 'Fetching…' : '↻ Refresh image'}
          </Btn>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
            <h1 style={{ fontSize: 24 }}>{plant.name}</h1>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {plant.status && <Badge color={statusColor(plant.status)}>{plant.status}</Badge>}
              <Btn size="sm" onClick={() => setEditModal(true)}>✎ Edit</Btn>
            </div>
          </div>
          {plant.variety && <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>{plant.variety}</div>}
          {plant.type && <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: '1rem' }}>{plant.type}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: '1rem' }}>
            {plant.location && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)' }}>Location: </span>{plant.location}</div>}
            {plant.sowDate && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)' }}>Planted: </span>{formatDate(plant.sowDate)}</div>}
            {plant.waterEvery && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)' }}>Water every: </span>{plant.waterEvery} days</div>}
            {totalHarvest > 0 && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)' }}>Total harvest: </span>{totalHarvest.toFixed(2)} kg</div>}
          </div>

          {plant.notes && <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{plant.notes}</p>}

          <div style={{ display: 'flex', gap: 8, marginTop: '1rem', flexWrap: 'wrap' }}>
            <Btn variant="primary" size="sm" onClick={() => setCareModal(true)}>+ Log care</Btn>
            <Btn size="sm" onClick={() => setHarvestModal(true)}>🌾 Log harvest</Btn>
            <Btn size="sm" variant="danger" onClick={() => { if (window.confirm(`Delete ${plant.name}?`)) { onDelete(plant.id); onBack(); } }}>Delete</Btn>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <Card>
          <SectionTitle>Care log</SectionTitle>
          {(plant.careLog || []).length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No care entries yet.</p>
          ) : [...(plant.careLog || [])].reverse().map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', minWidth: 70 }}>{c.date ? formatDate(c.date) : ''}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{c.type}</div>
                {c.note && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.note}</div>}
              </div>
            </div>
          ))}
        </Card>

        <Card>
          <SectionTitle>Harvest log</SectionTitle>
          {(plant.harvestLog || []).length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No harvests logged yet.</p>
          ) : [...(plant.harvestLog || [])].reverse().map(h => (
            <div key={h.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', minWidth: 70 }}>{h.date ? formatDate(h.date) : ''}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{h.amount} {h.unit}</div>
                {h.note && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{h.note}</div>}
              </div>
            </div>
          ))}
          {totalHarvest > 0 && <div style={{ paddingTop: 8, fontSize: 13, fontWeight: 500, color: 'var(--moss-600)' }}>Total: {totalHarvest.toFixed(2)} kg</div>}
        </Card>
      </div>

      {careModal && <LogCareModal plant={plant} onSave={addCare} onClose={() => setCareModal(false)} />}
      {harvestModal && <LogHarvestModal plant={plant} onSave={addHarvest} onClose={() => setHarvestModal(false)} />}
      {editModal && <EditPlantModal plant={plant} onSave={updated => { onUpdate(updated); setEditModal(false); }} onClose={() => setEditModal(false)} />}
    </div>
  );
}

export default function Plants({ state, dispatch }) {
  const [addModal, setAddModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  const { plants } = state;
  const types = ['All', ...new Set(plants.map(p => p.type).filter(Boolean))].sort();
  const filtered = plants
    .filter(p => filter === 'All' || p.type === filter)
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (selected) {
    const plant = plants.find(p => p.id === selected);
    if (plant) return (
      <PlantDetail
        plant={plant}
        onUpdate={updated => dispatch({ type: 'UPDATE_PLANT', plant: updated })}
        onDelete={id => dispatch({ type: 'DELETE_PLANT', id })}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 860 }} className="fade-up">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: 24, marginBottom: 4 }}>Plant journal</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{plants.length} plant{plants.length !== 1 ? 's' : ''} tracked</p>
        </div>
        <Btn variant="primary" onClick={() => setAddModal(true)}>+ Add plant</Btn>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search plants…" style={{ width: 200, fontSize: 13 }} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {types.map(t => (
            <button key={t} onClick={() => setFilter(t)} style={{
              fontSize: 12, padding: '4px 12px', borderRadius: 20, border: '1px solid', cursor: 'pointer',
              borderColor: filter === t ? 'var(--accent)' : 'var(--border-md)',
              background: filter === t ? 'var(--accent-light)' : 'transparent',
              color: filter === t ? 'var(--accent)' : 'var(--text-secondary)',
            }}>{t}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="🌱" title="No plants found"
          body={search ? 'Try a different search.' : 'Add your first plant to get started.'}
          action={!search && <Btn variant="primary" onClick={() => setAddModal(true)}>Add plant</Btn>} />
      ) : (
        <Card style={{ padding: '0.25rem 0' }}>
          {filtered.map(p => <PlantRow key={p.id} plant={p} onClick={() => setSelected(p.id)} />)}
        </Card>
      )}

      {addModal && (
        <AddPlantModal
          onSave={plant => { dispatch({ type: 'ADD_PLANT', plant }); setAddModal(false); }}
          onClose={() => setAddModal(false)}
        />
      )}
    </div>
  );
}
